import React, { useRef, useState, useMemo, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import WebFont from 'webfontloader';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import { dialogs, i18next, view, customization, bowser, i18n } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import upsell from 'src/js/modules/upsell';
import segment from 'src/js/modules/analytics/segment';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import isTextMatchingSearchQuery from 'src/js/modules/common/helpers/is-text-matching-search-query';
import FontsSettingsDialog from '../components/fonts-settings-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import helpers from '../helpers';
import constants from '../constants';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import fontSettingsSourceIds from '../constants/font-settings-source-ids';
import triggerIds from '../constants/trigger-ids';
import { setFontOptionVisible } from '../helpers/font-options-visibility-manager';
import useFontsData from '../helpers/use-fonts-data';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';
import getDefaultElementsStylesValues from '../helpers/get-default-elements-styles-values';
import getBasicLineHeightValues from '../helpers/get-basic-line-height-values';
import getInitialSearchQuery from '../helpers/get-initial-search-query';
import {
  elementTypeIds,
  fontStyleIdsMap,
  elementStylesMap,
  optionIdsMap,
} from '../constants/element-customization';
import { AVAILABLE_SURFACES } from '../../blocks/constants/common';

const {
  hostingPackageFeatureNames: { ADVANCED_WEBSITE_CUSTOMIZATION },
} = subscriptionManagerConstants;

const { useForm } = reactHookForm;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const {
  generateUniqueFontPairId,
  linkFont,
  getFontFallbackByCategory,
  prepareFontVariantsString,
  getCompiledFontPairConfig,
} = helpers;
const { DEFAULT_OFFSET_X, DEFAULT_OFFSET_Y } = constants;
const DIALOG_CLASS_NAME = 'ws-design-fonts-dialog';
const SELECT_CLASS_NAME = 'ws-ui-base-select';

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Fonts')],
  title: i18next.t('Edit your font pair'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t("Changing a font updates it wherever it's used on your website"),
  searchPlaceholder: i18next.t('Search...'),
  searchEmptyTitle: i18next.t('Sorry!'),
  searchEmptyText: i18next.t('Nothing was found'),
  elements: {
    heading1: i18next.t('Heading {number}', { number: 1 }),
    heading2: i18next.t('Heading {number}', { number: 2 }),
    heading3: i18next.t('Heading {number}', { number: 3 }),
    heading4: i18next.t('Heading {number}', { number: 4 }),
    heading5: i18next.t('Heading {number}', { number: 5 }),
    heading6: i18next.t('Heading {number}', { number: 6 }),
    paragraph: i18next.t('Paragraph'),
    blockquote: i18next.t('Quote'),
    button: i18next.t('Buttons'),
    navigation: i18next.t('Navigations'),
    headerPhone: `${i18next.t('Header')} → ${i18next.t('Phone number')}`,
    siteName: i18next.t('Site name'),
    label: `${i18next.t('Form')} → ${i18next.t('label')}`,
    input: `${i18next.t('Form')} → ${i18next.t('input')}`,
  },
  settings: {
    font: i18next.t('Font'),
    weight: i18next.t('Weight'),
    size: i18next.t('Size'),
    height: i18next.t('Height'),
    color: i18next.t('Color'),
    capitalization: i18next.t('Capitalization'),
    letterSpacing: i18next.t('Letter spacing'),
  },
  manageColorsButton: i18next.t('Manage colors'),
  fontSizeNotification: i18next.t(
    'Note: to ensure great accessibility font sizes over 64 are scaled down on tablets and mobile.'
  ),
  noResultsText: i18next.t('No result'),
});

const getAccordionSections = (captions) =>
  Object.values(elementTypeIds).map((id) => ({ id, title: captions[id] }));
const getSettingsTitles = (settingsCaptions) => Object.values(settingsCaptions);

const getFontPairConfig = (fontPair) => {
  if (fontPair.isCustom) {
    const config = { ...fontPair };

    if (!fontPair.basicLineHeightValues) {
      config.basicLineHeightValues = getBasicLineHeightValues(fontPair.variables);
    }

    return config;
  }

  return {
    ...fontPair,
    id: generateUniqueFontPairId(fontPair.id),
    parentId: fontPair.id,
    isCustom: true,
    basicLineHeightValues: getBasicLineHeightValues(fontPair.variables),
  };
};

const preloadFont = (font) => {
  const liveDocument = view.accessors.getLiveDocument();
  const config = {
    google: {
      families: [font],
    },
    classes: false,
    context: liveDocument.defaultView,
  };

  WebFont.load(config);
};

const FontsSettingsDialogContainer = ({
  fontPairId,
  initialActiveSection = elementTypeIds.HEADING_1,
  sourceId,
  initialSharedData = null,
  scrollTop = null,
  headerTextContentVisibility = true,
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const { data: fontsData, isSuccess } = useFontsData();
  const isReady = isSuccess && fontsData.length > 0;

  const {
    activeFontPairId,
    customFonts,
    allFonts,
    applyCustomFontPair,
    persistFontPairChangesForTextEditing = null,
    activeSections: sharedActiveSections = null,
    searchQuery: sharedSearchQuery = '',
    modifiedValuesMap: modifiedValues = null,
    offsetX = DEFAULT_OFFSET_X,
    offsetY = DEFAULT_OFFSET_Y,
  } = getSharedData();

  const isTextEditingSource = sourceId === fontSettingsSourceIds.TEXT_EDITING;

  const onAfterDialogClose = useCallback(() => {
    if (isTextEditingSource) {
      dialogs.operations.hide();
      return;
    }

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG, {
      sourceId: dialogCallSourceIds.EDIT_FONT_PAIR,
    });
  }, [isTextEditingSource]);

  const {
    control,
    reset,
    getFieldState,
    handleSubmit: manageSubmit,
    getValues,
    setValue,
  } = useForm();

  const captions = getCaptions();
  const accordionSections = getAccordionSections(captions.elements);
  const settingsTitles = getSettingsTitles(captions.settings);
  const initialSearchQuery = getInitialSearchQuery({
    sharedSearchQuery,
    isTextEditingSource,
    initialActiveSection,
    sections: accordionSections,
  });
  const limits = getCurrentPackageFeatureLimits();
  const isAdvancedFontCustomizationAvailable = limits.advancedWebsiteCustomization.available;
  const fontPair = allFonts?.find(({ id }) => id === fontPairId);
  const currentColorPalette = useSelector(customization.accessors.getCurrentColorPalette);
  const locale = useSelector(i18n.selectors.getLocale);

  const selectedFontPair = fontPair?.isCustom ? getCompiledFontPairConfig(fontPair) : fontPair;
  const fontPairConfig = useMemo(
    () => (selectedFontPair ? getFontPairConfig(selectedFontPair) : null),
    [selectedFontPair]
  );

  const fontPairVariables = useRef(selectedFontPair?.variables);
  const modifiedVariables = useRef([]);
  const preloadedFonts = useRef({});
  const contentRef = useRef(null);
  const isFontSettingsInitialized = useRef(false);

  // Keep the latest fonts data in a ref so the stable IntersectionObserver below reads the current
  // data instead of the empty array captured on the first render (fonts load lazily now).
  const fontsDataRef = useRef(fontsData);
  fontsDataRef.current = fontsData;

  const [isDevMode] = useState(() => isDeveloperMode());
  const [activeSections, setActiveSections] = useState(
    sharedActiveSections || [initialActiveSection]
  );
  const [dialogPosition, setDialogPosition] = useState({
    offsetX,
    offsetY,
  });
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  const filteredAccordionSections = useMemo(() => {
    if (!searchQuery.trim()) {
      return accordionSections;
    }

    const hasSettingsMatch = settingsTitles.some((title) =>
      isTextMatchingSearchQuery(searchQuery, title)
    );

    if (hasSettingsMatch) {
      return accordionSections;
    }

    return accordionSections.filter(({ title }) => isTextMatchingSearchQuery(searchQuery, title));
  }, [accordionSections, searchQuery, settingsTitles]);
  const [isVisibleHeaderTextContent, setIsVisibleHeaderTextContent] = useState(
    headerTextContentVisibility
  );

  const updateSharedState = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      sourceId,
      ...customData,
    });
  };

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);

    if (query.trim()) {
      trackEvent(events.DESIGN_SETTINGS_SEARCH_PERFORMED, {
        sourceId: triggerIds.EDIT_FONT_PAIR,
        searchTerm: query,
      });
    }

    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setIsVisibleHeaderTextContent(true);
  };

  const handleSearchQueryClear = () => {
    setSearchQuery('');
    contentRef.current?.scrollTo({ top: 0 });
  };

  const handleSearchFocus = () => {
    trackEvent(events.DESIGN_SETTINGS_SEARCH_CLICKED, {
      sourceId: triggerIds.EDIT_FONT_PAIR,
    });
  };

  const handleSubmit = (data, event) => {
    if (isAdvancedFontCustomizationAvailable || isDevMode) {
      const dirtyFields = Object.keys(data).filter((key) => getFieldState(key).isDirty);
      const fontPairAdjusted = !!dirtyFields.length;

      modifiedVariables.current = Array.from(
        new Set([...modifiedVariables.current, ...dirtyFields])
      );

      const { primaryFontFamily, secondaryFontFamily } = fontPairVariables.current;

      const newUsedFontFamilies = modifiedVariables.current
        .filter((variable) => variable.endsWith(elementStylesMap.FONT_FAMILY))
        .map((variable) =>
          fontPairVariables.current[variable].split(',')[0].replace(/"/g, '').trim()
        )
        .filter(
          (family) =>
            // we need to filter out font families that start with "var" as we can have some overrides
            // as `var(--primary-font-family)` or `var(--secondary-font-family)` after migration
            !family.startsWith('var') &&
            family !== primaryFontFamily &&
            family !== secondaryFontFamily
        );

      let { usedFonts } = fontPairVariables.current;

      if (newUsedFontFamilies.length) {
        const newUsedFontsString = Array.from(new Set(newUsedFontFamilies))
          .map((fontName) => {
            const { family, variants } = fontsData.find((font) => font.family === fontName);
            return `${family.replace(/ /g, '+')}:${prepareFontVariantsString(variants)}`;
          })
          .join(' | ');

        const usedFontsSet = Array.from(
          new Set(`${usedFonts} | ${newUsedFontsString}`.split(' | '))
        );

        usedFonts = usedFontsSet
          .filter((font) => {
            const family = font.split(':')[0].replace(/\+/g, ' ');
            return (
              family === primaryFontFamily ||
              family === secondaryFontFamily ||
              newUsedFontFamilies.includes(family)
            );
          })
          .join(' | ');
      }

      const variableOverrides = modifiedVariables.current.reduce((acc, variable) => {
        acc[variable] = fontPairVariables.current[variable];
        return acc;
      }, {});

      const { variables, ...restConfig } = fontPairConfig;

      const newConfig = {
        ...restConfig,
        variableOverrides: { ...variableOverrides, usedFonts },
      };

      applyCustomFontPair({ fontPairConfig: newConfig });

      const filteredCustomFonts = customFonts.filter(
        (customFont) => customFont.id !== fontPairConfig.id
      );

      const updatedCustomFontsList = [...filteredCustomFonts, newConfig];

      if (persistFontPairChangesForTextEditing) {
        persistFontPairChangesForTextEditing(updatedCustomFontsList, newConfig);
      }

      updateSharedState({
        customFonts: updatedCustomFontsList,
        activeFontPairId: fontPairConfig.id,
        fontPairAdjusted,
        activeSections: null,
        modifiedValuesMap: null,
        searchQuery: '',
      });

      onDialogMainAction({
        triggerId: event ? SUBMIT_BUTTON_TRIGGER_ID : DIALOG_BACKGROUND_TRIGGER_ID,
        sourceId,
        fontPairAdjusted,
      });
    } else {
      applyCustomFontPair({ fontPairId: activeFontPairId });
    }

    onAfterDialogClose();
  };

  const handleCancel = () => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      activeSections: null,
      modifiedValuesMap: null,
      searchQuery: '',
    });

    applyCustomFontPair({ fontPairId: activeFontPairId });

    onDialogCancel();

    onAfterDialogClose();
  };

  const handleFeatureAvailability = (
    elementTypeId,
    elementStyle,
    callback = Function.prototype
  ) => {
    trackEvent(events.DESIGN_SETTINGS_FONT_SETTINGS_OPTION_CLICKED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG,
      optionId: optionIdsMap[elementStyle],
      fontStyleId: fontStyleIdsMap[elementTypeId],
    });

    if (isDevMode) {
      callback();
    }

    upsell.operations
      .requestFeatureAvailability(ADVANCED_WEBSITE_CUSTOMIZATION)
      .then(callback)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
        document.activeElement.blur();
      });
  };

  const onManageColorsClick = (elementTypeId) => {
    const values = getValues();
    const modifiedValuesMap = Object.keys(values).reduce((acc, fieldName) => {
      if (!getFieldState(fieldName).isDirty) return acc;

      if (fieldName.endsWith(elementStylesMap.LINE_HEIGHT) && values[fieldName] === 'Auto') {
        acc[fieldName] = fontPairConfig.basicLineHeightValues[fieldName];
        return acc;
      }

      acc[fieldName] = values[fieldName];
      return acc;
    }, {});

    let colorsDialogSearchQuery;

    switch (elementTypeId) {
      // This is a temporary solution to set correct search query for buttons and navigation sections in the colors dialog
      // until we add a separate font variables for the header and footer navigations, primary and secondary buttons
      case elementTypeIds.BUTTON:
        if (locale === 'en' || locale === 'en-us') {
          colorsDialogSearchQuery = i18next.t('Button');
        } else {
          colorsDialogSearchQuery = '';
        }

        break;
      case elementTypeIds.NAVIGATION:
        if (locale === 'en' || locale === 'en-us') {
          colorsDialogSearchQuery = i18next.t('Navigation');
        } else {
          colorsDialogSearchQuery = '';
        }

        break;
      case elementTypeIds.PHONE_NUMBER:
        colorsDialogSearchQuery = i18next.t('Phone number');
        break;
      case elementTypeIds.FORM_LABEL:
        colorsDialogSearchQuery = i18next.t('Input label');
        break;
      case elementTypeIds.FORM_INPUT:
        colorsDialogSearchQuery = i18next.t('Input field text');
        break;
      default:
        colorsDialogSearchQuery = accordionSections.find(({ id }) => id === elementTypeId).title;
    }

    updateSharedState({
      activeSections,
      activeElementTypeId: elementTypeId,
      fontPairId,
      modifiedValuesMap,
      originPalette: currentColorPalette,
      searchQuery: colorsDialogSearchQuery,
      isDeveloperMode: isDevMode,
      ...(isTextEditingSource && {
        allFonts,
        customFonts,
        activeFontPairId,
        applyCustomFontPair,
        persistFontPairChangesForTextEditing,
      }),
    });

    trackEvent(events.DESIGN_SETTINGS_FONT_SETTINGS_OPTION_CLICKED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG,
      optionId: optionIdsMap.fontColor,
      fontStyleId: fontStyleIdsMap[elementTypeId],
    });

    dialogs.operations.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
      triggerId: triggerIds.EDIT_FONT_PAIR,
      colorSchemeId: AVAILABLE_SURFACES[0],
      scrollTop: contentRef.current.scrollTop,
      headerTextContentVisibility: isVisibleHeaderTextContent,
      preserveSharedData: true,
    });
  };

  const handleFontFamilyChange = (elementTypeId, selectedFont) => {
    const fontVariable = `${elementTypeId}${elementStylesMap.FONT_FAMILY}`;
    const weightVariable = `${elementTypeId}${elementStylesMap.FONT_WEIGHT}`;

    const { category, variants } = fontsData.find(({ family }) => family === selectedFont);

    const selectedFontFallback = getFontFallbackByCategory(category);
    const elementFontValue = `"${selectedFont}", ${selectedFontFallback}`;

    const elementFontWeight = getValues(weightVariable);
    let newFontWeight;

    if (variants.includes(elementFontWeight)) {
      newFontWeight = elementFontWeight;
    } else if (variants.includes('400')) {
      newFontWeight = '400';
      setValue(weightVariable, newFontWeight, { shouldDirty: true });
    } else if (variants.includes('regular')) {
      newFontWeight = 'regular';
      setValue(weightVariable, newFontWeight, { shouldDirty: true });
    } else {
      [newFontWeight] = variants;
      setValue(weightVariable, newFontWeight, { shouldDirty: true });
    }

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [fontVariable]: elementFontValue,
      [weightVariable]: newFontWeight,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleFontWeightChange = (weightVariable, selectedWeight) => {
    fontPairVariables.current = {
      ...fontPairVariables.current,
      [weightVariable]: selectedWeight,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleFontSizeChange = (sizeVariable, selectedSize) => {
    const sizeValue = sizeVariable.startsWith('heading') ? selectedSize : `${selectedSize}px`;

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [sizeVariable]: sizeValue,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleLineHeightChange = (heightVariable, selectedHeight) => {
    if (!selectedHeight) {
      return;
    }

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [heightVariable]: `${selectedHeight}em`,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleTextTransformChange = (
    elementTypeId,
    capitalizationVariable,
    selectedCapitalizationValue
  ) => {
    const callback = () => {
      setValue(capitalizationVariable, selectedCapitalizationValue, { shouldDirty: true });

      fontPairVariables.current = {
        ...fontPairVariables.current,
        [capitalizationVariable]: selectedCapitalizationValue,
      };

      applyCustomFontPair({
        fontPairConfig: {
          ...fontPairConfig,
          variables: fontPairVariables.current,
        },
      });
    };

    handleFeatureAvailability(elementTypeId, elementStylesMap.TEXT_TRANSFORM, callback);
  };

  const handleLetterSpacingChange = (letterSpacingVariable, selectedLetterSpacing) => {
    const letterSpacingValue = String(selectedLetterSpacing).endsWith('em')
      ? selectedLetterSpacing
      : `${selectedLetterSpacing}em`;

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [letterSpacingVariable]: letterSpacingValue,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleDragEnd = (_, { x, y }) => {
    setDialogPosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const observer = useMemo(() => {
    const root = document.querySelector(`.${DIALOG_CLASS_NAME} .${SELECT_CLASS_NAME}`);

    return new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            const fontFamily = target.id;
            setFontOptionVisible(fontFamily);
            const font = fontsDataRef.current.find(({ family }) => family === fontFamily);

            if (font) {
              linkFont(font.family);

              if (!preloadedFonts.current[fontFamily]) {
                const fontToPreload = `${font.family.replace(/ /g, '+')}:${font.variants.join(
                  ','
                )}`;
                preloadFont(fontToPreload);
                preloadedFonts.current[fontFamily] = true;
              }
            }

            obs.unobserve(target);
          }
        });
      },
      {
        root,
        rootMargin: '1000px',
        scrollMargin: '1000px',
      }
    );
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => observer.disconnect(), [observer]);

  useEffect(() => {
    const timeout = bowser.safari ? 100 : 0;

    if (scrollTop && contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollTo(0, scrollTop);
      }, timeout);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [contentRef.current]);

  useEffect(() => {
    const element = contentRef?.current;

    if (!element) return;

    const scrollHandler = () => {
      const scrollBottom = element.scrollHeight - element.clientHeight;
      const isAtBottom = element.scrollTop >= scrollBottom - 1;

      if (activeSections.length < 1) {
        setIsVisibleHeaderTextContent(true);

        return;
      }

      if (isAtBottom) return; // Skip if at bottom to avoid flicker

      if (element.scrollTop > 150) {
        setIsVisibleHeaderTextContent(false);
      } else if (element.scrollTop < 50) {
        setIsVisibleHeaderTextContent(true);
      }
    };

    element.addEventListener('scroll', scrollHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener('scroll', scrollHandler);
    };
  }, [activeSections]);

  useLayoutEffect(() => {
    if (!isReady) {
      return;
    }

    if (initialSharedData) {
      resolveSharedData({
        sourceId,
        ...initialSharedData,
      });
    }

    if (isFontSettingsInitialized.current) {
      return;
    }

    if (!allFonts?.length || !applyCustomFontPair || !selectedFontPair) {
      return;
    }

    isFontSettingsInitialized.current = true;
    fontPairVariables.current = selectedFontPair.variables;

    if (!modifiedValues) {
      applyCustomFontPair({ fontPairId });
    }

    if (selectedFontPair.variableOverrides) {
      modifiedVariables.current = Object.keys(selectedFontPair.variableOverrides);
    }

    const defaultValues = getDefaultElementsStylesValues(
      selectedFontPair.variables,
      modifiedVariables.current
    );

    reset(defaultValues);

    if (modifiedValues) {
      const processedModifiedValues = { ...modifiedValues };

      Object.entries(processedModifiedValues).forEach(([key, value]) => {
        setValue(key, value, { shouldDirty: true });

        if (key.endsWith(elementStylesMap.FONT_FAMILY)) {
          const { category } = fontsData.find(({ family }) => family === value);
          const fontFallback = getFontFallbackByCategory(category);
          processedModifiedValues[key] = `"${value}", ${fontFallback}`;
        }

        if (key.endsWith(elementStylesMap.LETTER_SPACING)) {
          const letterSpacingValue = String(value).endsWith('em') ? value : `${value}em`;
          processedModifiedValues[key] = letterSpacingValue;
        }
      });

      fontPairVariables.current = {
        ...fontPairVariables.current,
        ...processedModifiedValues,
      };

      applyCustomFontPair({
        fontPairConfig: {
          ...fontPairConfig,
          variables: fontPairVariables.current,
        },
      });
    }
  }, [isReady, allFonts, applyCustomFontPair, selectedFontPair]);

  if (!fontPairConfig) {
    return null;
  }

  return (
    <FontsSettingsDialog
      isLoading={!isReady}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      control={control}
      fontsData={fontsData}
      fontObserver={observer}
      contentRef={contentRef}
      activeAccordionItems={activeSections}
      accordionSections={filteredAccordionSections}
      isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
      getValues={getValues}
      basicLineHeightValues={fontPairConfig.basicLineHeightValues}
      onToggleAccordionItem={setActiveSections}
      onSubmit={manageSubmit(handleSubmit)}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      handleFeatureAvailability={handleFeatureAvailability}
      handlers={{
        handleFontFamilyChange,
        handleFontWeightChange,
        handleFontSizeChange,
        handleLineHeightChange,
        handleTextTransformChange,
        handleLetterSpacingChange,
      }}
      onManageColorsClick={onManageColorsClick}
      searchQuery={searchQuery}
      handleSearchQueryChange={handleSearchQueryChange}
      handleSearchQueryClear={handleSearchQueryClear}
      handleSearchFocus={handleSearchFocus}
    />
  );
};

FontsSettingsDialogContainer.propTypes = {
  fontPairId: PropTypes.string.isRequired,
  initialActiveSection: PropTypes.string,
  sourceId: PropTypes.string.isRequired,
  initialSharedData: PropTypes.shape({
    activeFontPairId: PropTypes.string,
    customFonts: PropTypes.arrayOf(PropTypes.shape({})),
    allFonts: PropTypes.arrayOf(PropTypes.shape({})),
    applyCustomFontPair: PropTypes.func,
  }),
  scrollTop: PropTypes.number,
  headerTextContentVisibility: PropTypes.bool,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default FontsSettingsDialogContainer;
